<template>
  <div class="fix-pdc">
    <div class="card">
      <div class="grid">
        <!-- Header -->
        <div class="col-12">
          <div class="fix-header">
            <div class="fix-title">
              <i class="pi pi-wrench"></i>
              <span>Fix PdC</span>
            </div>
            <span class="fix-sub">
              Carica uno o più XML di risposta SII (flusso 0100, SM1/RT1) di flussi andati in errore:
              ricostruisce la riga mancante in <code>_out</code> e deposita il CSV in uscita.
            </span>
          </div>
        </div>

        <!-- Step 1: selezione file -->
        <div class="col-12">
          <div class="panel">
            <div class="panel-title">1 · Seleziona i file XML</div>
            <div class="info-note">
              <i class="pi pi-info-circle"></i>
              <span>
                Dal portale SII scarica il <strong>«Messaggio SII»</strong> relativo all'<strong>ammissibilità</strong> della pratica.
                Da <strong>non</strong> confondere con il <strong>«Messaggio SII PdC»</strong>.
                Puoi selezionarne <strong>più di uno</strong> (massimo {{ MAX_FILE }} per volta).
              </span>
            </div>
            <div class="upload-row">
              <label class="file-pick">
                <i class="pi pi-file-import"></i>
                <span class="file-name">{{ etichettaFile }}</span>
                <input ref="fileInput" type="file" multiple accept=".xml,text/xml,application/xml" @change="onFile" />
              </label>
              <Button label="Analizza" icon="pi pi-search"
                :disabled="!files.length || loading" :loading="loading && phase === 'preview'" @click="analizza" />
              <Button v-if="files.length || preview || esiti" label="Reset" icon="pi pi-times"
                class="p-button-text" :disabled="loading" @click="reset" />
            </div>

            <ul v-if="files.length" class="file-list">
              <li v-for="(f, i) in files" :key="i"><i class="pi pi-file"></i> {{ f.name }}</li>
            </ul>

            <div v-if="error" class="banner-error mt-3">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ error }}</span>
            </div>
            <div v-if="avviso" class="banner-warn mt-3">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ avviso }}</span>
            </div>
          </div>
        </div>

        <!-- Step 2: anteprima -->
        <div class="col-12" v-if="preview && !esiti">
          <div class="panel">
            <div class="panel-title">
              2 · Anteprima
              <span class="muted">· {{ preview.totali.totale }} file · {{ preview.totali.fixabile }} fixabili</span>
            </div>

            <DataTable :value="preview.risultati" dataKey="file_name" responsiveLayout="scroll" class="p-datatable-sm">
              <Column field="file_name" header="File"></Column>
              <Column header="POD">
                <template #body="{ data }"><span class="mono">{{ podDi(data) }}</span></template>
              </Column>
              <Column header="CP_UTENTE">
                <template #body="{ data }"><span class="mono">{{ cpDi(data) }}</span></template>
              </Column>
              <Column header="Servizio">
                <template #body="{ data }">{{ servizioDi(data) }}</template>
              </Column>
              <Column header="Ammissibilità">
                <template #body="{ data }">
                  <span v-if="ammDi(data) !== null" class="pill" :class="ammDi(data) ? 'pill-ok' : 'pill-ko'">
                    {{ ammDi(data) ? 'Ammessa' : 'Non ammessa' }}
                  </span>
                  <span v-else class="muted">—</span>
                </template>
              </Column>
              <Column header="Stato">
                <template #body="{ data }">
                  <span class="pill" :class="classeStato(data.stato)">{{ labelStato(data.stato) }}</span>
                  <div class="riga-msg">{{ data.messaggio }}</div>
                </template>
              </Column>
            </DataTable>

            <div class="confirm-row" v-if="preview.totali.fixabile > 0">
              <Button :label="'Conferma fix (' + preview.totali.fixabile + ')'" icon="pi pi-check"
                :loading="loading && phase === 'upload'" @click="conferma" />
              <span class="confirm-hint">
                Verranno fixati solo i file <strong>fixabili</strong>; gli altri vengono ignorati senza effetti.
              </span>
            </div>
            <div class="banner-warn" v-else>
              <i class="pi pi-info-circle"></i>
              <span>Nessun file fixabile in questa selezione.</span>
            </div>
          </div>
        </div>

        <!-- Step 3: esiti -->
        <div class="col-12" v-if="esiti">
          <div class="panel">
            <div class="panel-title">
              3 · Esito
              <span class="muted">
                · {{ esiti.totali.ok }} fixati · {{ esiti.totali.gia_presente }} già presenti
                · {{ esiti.totali.non_inviato }} non pertinenti · {{ esiti.totali.errori }} errori
              </span>
            </div>

            <DataTable :value="esiti.risultati" dataKey="file_name" responsiveLayout="scroll" class="p-datatable-sm">
              <Column field="file_name" header="File"></Column>
              <Column header="POD">
                <template #body="{ data }"><span class="mono">{{ podDi(data) }}</span></template>
              </Column>
              <Column header="Esito">
                <template #body="{ data }">
                  <span class="pill" :class="classeEsito(data.esito)">{{ labelEsito(data.esito) }}</span>
                  <div class="riga-msg">{{ data.messaggio }}</div>
                  <div class="riga-msg mono" v-if="data.csv_uscita">CSV: {{ data.csv_uscita }}</div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GestionePdcFixService from '@/services/ask-pratiche/gestione-pdc-fix';

// Allineato a AdminPDCFixController::MAX_FILE (e a max_file_uploads di PHP).
const MAX_FILE = 20;

const STATO_LABEL = {
  fixabile: 'Fixabile',
  gia_presente: 'Già presente',
  non_inviato: 'Non pertinente',
  errore: 'Errore',
};

const ESITO_LABEL = {
  ok: 'Fixato',
  gia_presente: 'Già presente',
  non_inviato: 'Non pertinente',
  errore: 'Errore',
};

export default {
  name: 'FixPDC',
  data() {
    return {
      MAX_FILE,
      files: [],
      loading: false,
      phase: null, // 'preview' | 'upload'
      error: null,
      avviso: null,
      preview: null, // { risultati, totali, avviso }
      esiti: null,   // { risultati, totali, avviso }
    };
  },
  computed: {
    etichettaFile() {
      if (!this.files.length) return 'Scegli uno o più file .xml…';
      if (this.files.length === 1) return this.files[0].name;
      return this.files.length + ' file selezionati';
    },
  },
  created() {
    this.service = new GestionePdcFixService();
  },
  methods: {
    onFile(e) {
      const scelti = e.target.files ? Array.from(e.target.files) : [];
      this.files = scelti.slice(0, MAX_FILE);
      this.error = scelti.length > MAX_FILE
        ? 'Puoi caricare al massimo ' + MAX_FILE + ' file per volta: considerati i primi ' + MAX_FILE + '.'
        : null;
      this.avviso = null;
      this.preview = null;
      this.esiti = null;
    },
    async analizza() {
      if (!this.files.length) return;
      this.loading = true;
      this.phase = 'preview';
      this.error = null;
      this.esiti = null;
      try {
        const r = await this.service.preview(this.files);
        this.preview = r;
        this.avviso = r.avviso || null;
      } catch (e) {
        this.preview = null;
        this.error = this.extractError(e, "Errore durante l'analisi dei file.");
      } finally {
        this.loading = false;
        this.phase = null;
      }
    },
    async conferma() {
      if (!this.files.length) return;
      this.loading = true;
      this.phase = 'upload';
      this.error = null;
      try {
        // Si rimandano tutti: il backend ri-applica il gate e salta da sé i non fixabili.
        const r = await this.service.upload(this.files);
        this.esiti = r;
        this.avviso = r.avviso || null;
      } catch (e) {
        this.error = this.extractError(e, 'Errore durante il fix.');
      } finally {
        this.loading = false;
        this.phase = null;
      }
    },
    reset() {
      this.files = [];
      this.preview = null;
      this.esiti = null;
      this.error = null;
      this.avviso = null;
      // svuota l'input nativo così riselezionare gli stessi file rilancia @change
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    podDi(r) { return (r.record && r.record.COD_POD) || '—'; },
    cpDi(r) { return (r.record && r.record.CP_UTENTE) || '—'; },
    servizioDi(r) { return r.record ? r.record.COD_SERVIZIO + ' · ' + r.record.COD_FLUSSO : '—'; },
    ammDi(r) { return r.record ? r.record.VERIFICA_AMM === '1' : null; },
    labelStato(s) { return STATO_LABEL[s] || s; },
    labelEsito(s) { return ESITO_LABEL[s] || s; },
    classeStato(s) {
      if (s === 'fixabile') return 'pill-ok';
      if (s === 'errore') return 'pill-ko';
      return 'pill-neutro';
    },
    classeEsito(s) {
      if (s === 'ok') return 'pill-ok';
      if (s === 'errore') return 'pill-ko';
      return 'pill-neutro';
    },
    extractError(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.error) || fallback;
    },
  },
};
</script>

<style scoped>
/* Header */
.fix-header {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--surface-border);
}
.fix-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.4rem;
  font-weight: 600;
}
.fix-title .pi {
  color: var(--text-color-secondary);
}
.fix-sub {
  color: var(--text-color-secondary);
  font-size: 0.92rem;
}

/* Pannelli */
.panel {
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 1.35rem 1.5rem;
}
.panel-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 1.1rem;
}
.muted {
  color: var(--text-color-secondary);
  font-weight: 400;
}
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: var(--surface-100);
  padding: 0.1rem 0.4rem;
  border-radius: 6px;
  font-size: 0.88em;
}

/* Upload */
.upload-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}
.file-pick {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1 1 22rem;
  min-width: 0;
  padding: 0.65rem 1rem;
  border: 1px dashed var(--surface-border);
  border-radius: 10px;
  cursor: pointer;
  color: var(--text-color-secondary);
  transition: border-color 0.2s ease, background 0.2s ease;
}
.file-pick:hover {
  border-color: var(--primary-color, #2196f3);
  background: var(--surface-50, #fafafa);
}
.file-pick .file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-pick input[type='file'] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* Record */
.rec-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1.1rem 2.5rem;
}
.rec {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.rl {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}
.rv {
  font-weight: 600;
  color: var(--text-color);
}
.pill {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
}
.pill-ok {
  background: rgba(30, 169, 124, 0.14);
  color: #1ea97c;
}
.pill-ko {
  background: rgba(226, 75, 74, 0.14);
  color: #e24b4a;
}

.confirm-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--surface-border);
}
.confirm-hint {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}

/* Banner */
.banner-error,
.banner-warn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
}
.banner-error {
  background: var(--red-50, #fef2f2);
  color: var(--red-700, #b42318);
  border: 1px solid var(--red-200, #fecaca);
  margin-bottom: 0;
}
.banner-warn {
  background: var(--yellow-50, #fffbeb);
  color: var(--yellow-700, #b45309);
  border: 1px solid var(--yellow-200, #fde68a);
}

/* Avviso informativo (quale messaggio scaricare dal portale SII) */
.info-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1.1rem;
  border-radius: 10px;
  background: var(--blue-50, #eff6ff);
  color: var(--blue-700, #1d4ed8);
  border: 1px solid var(--blue-200, #bfdbfe);
  font-size: 0.9rem;
  line-height: 1.45;
}
.info-note .pi {
  margin-top: 0.15rem;
}

/* Esito */
.success-panel {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}
.success-panel .pi-check-circle {
  font-size: 2rem;
  color: #1ea97c;
}
.success-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.success-msg {
  margin: 0 0 0.35rem;
}

.mt-3 {
  margin-top: 1rem;
}
</style>

<style scoped>
/* Multi-file: elenco dei file selezionati */
.file-list {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.file-list li {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}
/* Stato neutro (già presente / non pertinente) */
.pill-neutro {
  background: var(--surface-200);
  color: var(--text-color-secondary);
}
/* Messaggio sotto il pill nelle tabelle esito */
.riga-msg {
  font-size: 0.78rem;
  color: var(--text-color-secondary);
  margin-top: 0.2rem;
  white-space: normal;
}
</style>
